import * as React from 'react';
import { useContext, useMemo, useState } from 'react';
import { DocumentSetupContext } from './DocumentPageComponent';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { useTranslate } from 'sbx-crm-menu-ui';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../ui/accordion';
import { ClipboardPenLine, FileSearch2, Settings } from 'lucide-react';
import RenderFormFieldsComponent from '../../shared/renderFormFieldsComponent/RenderFormFieldsComponent';
import RenderFormFieldComponent from '../../shared/renderFormFieldsComponent/RenderFormFieldComponent';
import DynamicInputList from '../../shared/DynamicInputListComponent';
import { AppAgentContext } from '../../../context/ContextProvider';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';
import { analyzeDocumentServiceV2 } from '../../../services/DocumentService';
import ButtonComponent from '../../shared/ButtonComponent';

const classNameLabel = 'form-input-label';
const DocumentAnalysisComponent = () => {
    const { state: { session: { user_id } } } = useContext(AppAgentContext);
    const { agents, control, watch, setValue, getValues } = useContext(DocumentSetupContext);
    const { t: document } = useTranslate('document');
    const { t: common } = useTranslate('common');
    const { t: agent } = useTranslate('agent');
    const [searchParams, setSearchParams] = useSearchParams();
    const [classificationInput, setClassificationInput] = useState<string>('');
    const [examplesInput, setExamplesInput] = useState<string>('');
    const [loading, setLoading] = useState(false);
    const classificationList = watch('analysis.doc_analysis_params.contract_analysis_classification') || [];
    const examplesList = watch('analysis.doc_analysis_params.contract_analysis_examples') || [];

    const fields = useMemo(() => {
        return {
            setup_fields: [
                {
                    name: 'analysis.vs_config.chunk_size',
                    label: common('chunk_size'),
                    classNameLabel,
                    placeholder: common('chunk_size'),
                    type: 'number',
                    rules: {
                        required: false,
                    },
                },
                {
                    name: 'analysis.vs_config.chunk_overlap',
                    label: common('chunk_overlap'),
                    classNameLabel,
                    placeholder: common('chunk_overlap'),
                    type: 'number',
                    rules: {
                        required: false,
                    },
                },
            ],
        };
    }, []);

    const analyzeDocument = async () => {
        const analyze = { ...getValues('analysis'), user_id };
        if (!analyze.agent_id) {
            toast.error(agent('select_agent_warning'));
            return;
        }
        setLoading(true);
        const res = await analyzeDocumentServiceV2(analyze);
        if (res?.success) {
            searchParams.set('mode', 'history');
            setSearchParams(searchParams);
        }else{
            toast.error(res?.message || "Error")
            setLoading(false)
        }
    };

    return (
        <Card className="bg-gradient-to-br from-pink-100 to-purple-100 shadow-xl">
            <CardHeader className="flex !flex-row items-center justify-between">
                <CardTitle className="text-2xl font-bold text-purple-800">{
                    document('document_analysis')
                }</CardTitle>

                <ButtonComponent
                    loading={loading}
                    disabled={loading}
                    onClick={analyzeDocument}
                    label={<>
                        <FileSearch2 size={20} className="mr-2" /> {document('analyze_document')}
                    </>}
                    className="bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white font-semibold py-2 px-4 rounded-full transition-all duration-300 transform hover:scale-105"
                />
            </CardHeader>
            <CardContent className="flex flex-col gap-2">

                <RenderFormFieldComponent item={{
                    classNameLabel,
                    type: 'select',
                    options: agents,
                    name: 'analysis.agent_id',
                    label: agent('agents'),
                    rules: {
                        required: true,
                    },
                }} control={control} />


                <Card>
                    <CardContent className="py-0">
                        <Accordion type="single" collapsible className="w-full">
                            <AccordionItem value="additional-features" className="border-b-0">
                                <AccordionTrigger
                                    className="text-lg font-semibold text-purple-700 hover:text-purple-900">
                                    <div className="flex items-center capitalize">
                                        <ClipboardPenLine className="w-5 h-5 mr-2" />
                                        {common('setup_text')}
                                    </div>
                                </AccordionTrigger>
                                <AccordionContent className=" space-y-4">
                                    <RenderFormFieldsComponent fields={fields['setup_fields'] ?? []}
                                                               control={control} />
                                </AccordionContent>
                            </AccordionItem>
                        </Accordion>
                    </CardContent>
                </Card>

                <Card>
                    <CardContent className="py-0">
                        <Accordion type="single" collapsible className="w-full">
                            <AccordionItem value="setup-review" className="border-b-0">
                                <AccordionTrigger
                                    className="text-lg font-semibold text-purple-700 hover:text-purple-900">
                                    <div className="flex items-center capitalize">
                                        <Settings className="w-5 h-5 mr-2" />
                                        {common('setup_review')}
                                    </div>
                                </AccordionTrigger>
                                <AccordionContent className="space-y-4">
                                    <RenderFormFieldComponent
                                        item={{
                                            classNameLabel,
                                            type: 'textarea',
                                            name: 'analysis.doc_analysis_params.contract_analysis_revision',
                                            label: document('analysis_rules'),
                                        }}
                                        control={control}
                                    />

                                    <DynamicInputList
                                        label={document('classification')}
                                        placeholder="Sin Riesgo, Poco Riesgoso, Riesgoso"
                                        control={control}
                                        name="analysis.doc_analysis_params.contract_analysis_classification"
                                        classNameLabel={classNameLabel}
                                        itemList={classificationList}
                                        setValue={setValue}
                                        inputValue={classificationInput}
                                        setInputValue={setClassificationInput}
                                    />

                                    <DynamicInputList
                                        label={common('examples')}
                                        placeholder={document('example_classification')}
                                        control={control}
                                        name="analysis.doc_analysis_params.contract_analysis_examples"
                                        classNameLabel={classNameLabel}
                                        itemList={examplesList}
                                        setValue={setValue}
                                        inputValue={examplesInput}
                                        setInputValue={setExamplesInput}
                                    />

                                    <RenderFormFieldComponent
                                        item={{
                                            classNameLabel,
                                            type: 'number',
                                            name: 'analysis.doc_analysis_params.sentiment_analysis_threshold',
                                            label: common('threshold'),
                                        }}
                                        control={control}
                                    />
                                </AccordionContent>
                            </AccordionItem>
                        </Accordion>
                    </CardContent>
                </Card>
            </CardContent>
        </Card>
    );
};

export default DocumentAnalysisComponent;